import dayjs, { Dayjs } from "dayjs"

import {
    Datasets,
    EB_ACU_DATASETS,
    EB_ACU_HIDDEN_FIELDS,
    EB_ACU_HIDDEN_SECTIONS,
    EB_ONLY_HIDDEN_FIELDS,
} from "../../../constants/enums"
import { objectOnCondition } from "../../../utils/general/generalUtils"

/** Strips HP-specific fields and sections from content for EB and ACU datasets */
const filterEbAcuContent = (
    content: Record<string, Record<string, number | string>>,
    dataset: Datasets
): Record<string, Record<string, number | string>> => {
    if (!EB_ACU_DATASETS.includes(dataset)) return content
    const isEb = dataset === Datasets.EB || dataset === Datasets.EB_DRAFT
    const hiddenFields = isEb
        ? [...EB_ACU_HIDDEN_FIELDS, ...EB_ONLY_HIDDEN_FIELDS]
        : EB_ACU_HIDDEN_FIELDS
    return Object.fromEntries(
        Object.entries(content)
            .filter(([section]) => !EB_ACU_HIDDEN_SECTIONS.includes(section))
            .map(([section, fields]) => [
                section,
                Object.fromEntries(
                    Object.entries(fields).filter(
                        ([key]) => !hiddenFields.includes(key)
                    )
                ),
            ])
    )
}

export interface IStandard {
    standard: string
    partialTT: string
    fullTT: string
}

interface IStandards {
    GB: IStandard[]
    NI: IStandard[]
}

interface ISingleDeviceInput {
    header: Record<string, string>
    status: IStatus
    content: Record<string, Record<string, number | string>>
    isSubscribed: boolean
    standards?: IStandards
    alert?: IAlert
    comments?: IComment[]
    pinnedComment?: string
    lastReadByUsername?: string
    lastReadByTimestamp?: string
}

export interface IStatus {
    "Compliance Status": string
    IsThreadUnread: boolean
    IsSubscribed: boolean
}

export interface IAlert {
    description: string
    alerterUsername: string | null
    alerterGivenName: string | null
    alerterFamilyName: string | null
    alerterOrganisation: string | null
    raisedAt: string
}

export interface IComment {
    id: string
    comment: string
    publishedAt: string
    firstName: string | null
    lastName: string | null
    initials: string | null
    groupType: string | null
    fromRequestor: boolean
}

interface IHeader {
    deviceRef: string
    manufacturer: string
    status: string
    isSubscribed?: boolean
    isThreadUnread?: boolean
    alert?: IAlert
    comments?: IComment[]
    pinnedComment?: string
    publishedDate: Dayjs
    supersededDeviceRef?: string
    draftVersionRef?: string
    draftAmendmentRef?: string
    attachmentUuid?: string
    lastReadBy?: string
    lastReadAt?: string
    linkedInverterRef?: string
}

export interface ISingleDevice {
    header: IHeader
    content: Record<string, Record<string, number | string>>
    standards?: IStandards
}

/**
 * The `V2G TTR Status` field on an EVAC device is overloaded: it can hold
 * either a linked inverter's DeviceRef (e.g. `TESTA/14608/V1`) or a plain
 * status word (e.g. `Compliant`). Only the DeviceRef form is useful for the
 * linked-inverter card, so we filter on the DeviceRef shape (contains `/`).
 */
const extractLinkedInverterRef = (
    raw: number | string | null | undefined
): string | undefined => {
    if (typeof raw !== "string") return undefined
    const trimmed = raw.trim()
    if (!trimmed.includes("/")) return undefined
    return trimmed
}

export const singleDeviceMapper = {
    /**
     * Maps single device response into UI data model. This applies to both
     * generation and demand
     * @param response - raw API response
     * @returns {ISingleDevice} - UI data model for a single device
     */
    mapDevice: (response: unknown, dataset: Datasets): ISingleDevice => {
        const resTyped = response as ISingleDeviceInput
        return {
            header: {
                deviceRef:
                    resTyped?.header?.["Model Ref"] ??
                    resTyped?.header?.["System Reference"],
                manufacturer: resTyped?.header?.Manufacturer,
                status: resTyped?.status?.["Compliance Status"],
                isSubscribed: resTyped?.status?.IsSubscribed ?? false,
                isThreadUnread: resTyped?.status?.IsThreadUnread ?? false,
                alert: resTyped?.alert,
                comments: resTyped?.comments,
                pinnedComment: resTyped?.pinnedComment,
                publishedDate: dayjs(
                    resTyped?.content?.["Other Details"]?.["Date Published"]
                ),
                supersededDeviceRef: resTyped?.header?.["Superseded Model Ref"],
                draftVersionRef: resTyped?.header?.["Draft Version Ref"],
                draftAmendmentRef: resTyped?.header?.["Draft Amendment Ref"],
                attachmentUuid: resTyped?.header?.["Attachment UUID"],
                lastReadBy: resTyped?.lastReadByUsername,
                lastReadAt: resTyped?.lastReadByTimestamp,
                linkedInverterRef: extractLinkedInverterRef(
                    resTyped?.content?.["Other Details"]?.["V2G TTR Status"]
                ),
            },
            content: filterEbAcuContent(resTyped?.content, dataset),
            ...objectOnCondition(
                dataset === Datasets.TTR || dataset === Datasets.TTR_DRAFT,
                {
                    standards: resTyped?.standards,
                } as {
                    [key: string]: unknown
                }
            ),
        }
    },
}
